import React, { useState, useEffect } from "react";
import { Stack, Typography, Box } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { InputForm } from "./InputForm";
import { ResultsDisplay } from "./ResultsDisplay";
import { IndicationDetails } from "./IndicationDetails";
import { DiseaseSelector } from "./DiseaseSelector";
import {
  MarketValueInputs,
  ProjectedFigures,
  Disease,
  SUGGESTED_DISEASES,
} from "../types";
import {
  calculateMarketValue,
  calculateTotalMarketValue,
} from "../utils/calculations";
import { enrichDiseaseWithIndicationData } from "../utils/indications";

export const Calculator: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDisease, setSelectedDisease] = useState<Disease | null>(null);
  const [inputs, setInputs] = useState<MarketValueInputs>({
    initialPatientPopulation: 0,
    newPatientsPerYear: 0,
    therapeuticPrice: 0,
    economicCostPerPatient: 0,
    // yearsOfLife: 0,
    projectionYears: 10,
    developmentCost: 250000000,
    marketPenetrationRate: 20,
    approvalProbability: 30,
    healthcareCostPerPatient: 100000,
  });
  const [projections, setProjections] = useState<ProjectedFigures[]>([]);
  const [totalMarketValue, setTotalMarketValue] = useState<number>(0);

  const handleDiseaseSelect = (disease: Disease) => {
    setSelectedDisease(disease);
    setSearchParams({ indication: disease.name });
    setInputs((prev) => ({
      ...prev,
      initialPatientPopulation: disease.initialPatientPopulation,
      newPatientsPerYear: disease.newPatientsPerYear,
      therapeuticPrice: disease.therapeuticPrice,
      economicCostPerPatient: disease.economicCostPerPatient,
      // yearsOfLife: disease.yearsOfLife,
      developmentCost: disease.developmentCost,
      healthcareCostPerPatient:
        disease.healthcareCostPerPatient ?? prev.healthcareCostPerPatient,
    }));
  };

  // Initialize from URL params
  useEffect(() => {
    const indicationName = searchParams.get("indication");
    let disease: Disease | undefined = SUGGESTED_DISEASES[0];

    // If there's a specific indication in the URL, find it.
    // If not, the disease is set to the first one in the list.
    if (indicationName) {
      disease = SUGGESTED_DISEASES.find(
        (d: Disease) => d.name === indicationName
      );
    }

    if (disease) {
      enrichDiseaseWithIndicationData(disease)
        .then((enrichedDisease) => {
          handleDiseaseSelect(enrichedDisease);
        })
        .catch((error) => {
          console.error("Error loading indication data:", error);
          handleDiseaseSelect(disease);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInputChange = (field: keyof MarketValueInputs, value: number) => {
    setInputs((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  useEffect(() => {
    // Calculate projections whenever inputs change
    const newProjections = calculateMarketValue(inputs);
    setProjections(newProjections);
    setTotalMarketValue(
      calculateTotalMarketValue(newProjections, inputs.developmentCost)
    );
  }, [inputs]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", lg: "row" },
        gap: 4,
        width: "100%",
        minHeight: "100%",
      }}
    >
      <Box
        sx={{
          flex: { xs: "1 1 auto", lg: "0 0 66.666667%" },
          maxWidth: { lg: "66.666667%" },
        }}
      >
        <Stack spacing={4} sx={{ height: "100%" }}>
          <Typography variant="h4" gutterBottom>
            Gene Therapy Market Value Calculator
          </Typography>
          <DiseaseSelector
            selectedDisease={selectedDisease}
            onDiseaseSelect={handleDiseaseSelect}
          />
          {selectedDisease && (
            <InputForm inputs={inputs} onInputChange={handleInputChange} />
          )}
          {projections.length > 0 && (
            <ResultsDisplay
              projections={projections}
              totalMarketValue={totalMarketValue}
            />
          )}
        </Stack>
      </Box>
      <Box
        sx={{
          flex: { xs: "1 1 auto", lg: "0 0 33.333333%" },
          maxWidth: { lg: "33.333333%" },
          position: "sticky",
          top: 24,
        }}
      >
        <IndicationDetails disease={selectedDisease} />
      </Box>
    </Box>
  );
};
